import { useIntl } from "react-intl";
import {
  AnalyticsComponentProvider,
  AnalyticsScreenProvider,
  Callout,
  Card,
  DropdownField,
  PageGridCell,
  PageGridRow,
  StandardLayout,
} from "@wayflyer/flyui";

import { LtvCac } from "../chart";

import type { LtvCacPeriod, LtvCacScreenProps, LtvCacChartType } from "./interface";

const fullWidth = { xxs: { span: 6 }, sm: { span: 8 }, md: { span: 12 } };
const filterWidth = { xxs: { span: 3 }, sm: { span: 4 }, md: { span: 3 } };

export const LtvCacScreen = ({
  chartDataLoading = false,
  chartDataError = "",
  mainChart,
  pageTitle,
  chartTypes,
  customerChart,
  selectedChartType = "ltv",
  customerTitle,
  selectedLtvPeriod,
  selectedAovPeriod,
  selectedFreqPeriod,
  periods,
  onChangeChart,
  onChangePeriod,
}: LtvCacScreenProps) => {
  const intl = useIntl();
  const mainTitle =
    mainChart?.referenceValueName || chartTypes.find((item) => item.id === selectedChartType)?.text || pageTitle;

  return (
    <AnalyticsScreenProvider screen="internal-ltv-cac">
      <StandardLayout title={pageTitle}>
        <AnalyticsComponentProvider component="shared-selectors">
          <PageGridRow>
            <PageGridCell layout={filterWidth}>
              <DropdownField
                initialValue={selectedChartType}
                disabled={false}
                readonly={false}
                name="chart-type"
                options={chartTypes}
                label={intl.formatMessage({
                  id: "Internal LTV / CAC: LTV/AOV/FREQUENCY",
                  defaultMessage: "LTV/AOV/FREQUENCY",
                })}
                onChange={(id) => onChangeChart(id as LtvCacChartType)}
              />
            </PageGridCell>
            <PageGridCell layout={filterWidth}>
              <DropdownField
                initialValue={selectedLtvPeriod}
                disabled={selectedChartType !== "ltv"}
                readonly={false}
                name="ltv-period"
                options={periods}
                label={intl.formatMessage({ id: "Internal LTV / CAC: LTV SELECTOR", defaultMessage: "LTV SELECTOR" })}
                onChange={(id) => onChangePeriod(selectedChartType, id as LtvCacPeriod)}
              />
            </PageGridCell>
            <PageGridCell layout={filterWidth}>
              <DropdownField
                initialValue={selectedAovPeriod}
                disabled={selectedChartType !== "aov"}
                readonly={false}
                name="aov-period"
                options={periods}
                label={intl.formatMessage({ id: "Internal LTV / CAC: AOV SELECTOR", defaultMessage: "AOV SELECTOR" })}
                onChange={(id) => onChangePeriod(selectedChartType, id as LtvCacPeriod)}
              />
            </PageGridCell>
            <PageGridCell layout={filterWidth}>
              <DropdownField
                initialValue={selectedFreqPeriod}
                disabled={selectedChartType !== "frequency"}
                readonly={false}
                name="freq-period"
                options={periods}
                label={intl.formatMessage({
                  id: "Internal LTV / CAC: FREQUENCY SELECTOR",
                  defaultMessage: "FREQUENCY SELECTOR",
                })}
                onChange={(id) => onChangePeriod(selectedChartType, id as LtvCacPeriod)}
              />
            </PageGridCell>
          </PageGridRow>
        </AnalyticsComponentProvider>
        {chartDataError ? (
          <PageGridRow>
            <PageGridCell layout={fullWidth}>
              <Callout variant="critical" description={chartDataError} />
            </PageGridCell>
          </PageGridRow>
        ) : null}
        {mainChart ? (
          <PageGridRow>
            <PageGridCell layout={fullWidth}>
              <AnalyticsComponentProvider component="cohort-chart">
                <Card slots={{ header: <Card.ChartHeader title={mainTitle} headingElement="h2" /> }}>
                  <LtvCac {...mainChart} loading={chartDataLoading} unavailable={Boolean(chartDataError)} />
                </Card>
              </AnalyticsComponentProvider>
            </PageGridCell>
          </PageGridRow>
        ) : null}
        {customerChart ? (
          <PageGridRow>
            <PageGridCell layout={fullWidth}>
              <AnalyticsComponentProvider component="acquisition-chart">
                <Card
                  slots={{
                    header: (
                      <Card.ChartHeader
                        title={customerTitle || customerChart.referenceValueName || pageTitle}
                        headingElement="h2"
                      />
                    ),
                  }}
                >
                  <LtvCac
                    {...customerChart}
                    loading={chartDataLoading}
                    unavailable={Boolean(chartDataError)}
                    isCustomer
                  />
                </Card>
              </AnalyticsComponentProvider>
            </PageGridCell>
          </PageGridRow>
        ) : null}
      </StandardLayout>
    </AnalyticsScreenProvider>
  );
};
